import type { Translate } from '@resource-kit/core';

/**
 * Enum-label lookup that FAILS LOUD on a value the option table does not carry
 * (#345, EQD §3 no-silent-fallback), rather than leaking the wire string into
 * operator-facing UI.
 *
 * At the FRAMEWORK-FREE root for the same reason `badge-variant` is: resolving
 * a label is a string operation, and the subtitle / detail-header sites that
 * call it hold no React context to read a translator from — which is why `t` is
 * a PARAMETER here instead of a `useResourceKit()` read. The host's
 * `lib/enum-label.ts` binds its own i18next singleton to it for the twelve
 * callers that predate the seam.
 *
 * `options` is typed structurally rather than as `EnumOption` so this module
 * names no schema package: both the legacy `EnumOption[]` and a model field's
 * resolved items satisfy it.
 *
 * `kind` is the diagnostic label (e.g. `'webhook status'`) used only in the
 * warn message — it keeps co-located callsites distinguishable in DevTools when
 * several fields drift at once.
 *
 * Known values MUST NOT warn: warn is reserved for drift. Empty / falsy input
 * is deliberately NOT special-cased — a caller with a legitimate "no value"
 * sentinel guards before calling, so this helper has exactly one shape.
 */
export function lookupEnumLabel(
  value: string,
  options: ReadonlyArray<{ value: string | number; label: string }>,
  kind: string,
  t: Translate,
): string {
  // Compare both sides as strings — an option's `value` is `string | number`
  // (catalog enums use the string variant; some numeric-keyed enums coexist).
  // The wire form is always a string when it reaches this helper.
  const option = options.find((o) => String(o.value) === value);
  if (!option) {
    console.warn(
      `[enum-label] Unknown ${kind} value '${value}' — frontend ` +
        `options table needs an entry, or the wire payload is malformed ` +
        `(see #345 for the cross-feature pattern, EQD §3 silent-fallback ` +
        `prohibition, docs/eng-quality-discipline.md).`,
    );
    return `[unknown:${value}]`;
  }
  return t(option.label);
}
